import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import ReliabilityBadge from "../components/ReliabilityBadge";
import AdminRemovePlayerButton from "../components/AdminRemovePlayerButton";

function StandingsTable({ leagueId }) {
  const { t } = useT();
  const { user } = useAuth();
  const myId = user && user !== false ? user.id : null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!leagueId) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/leagues/${leagueId}/standings`);
        if (alive) setRows(data);
      } catch (e) { /* noop */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [leagueId]);

  if (!leagueId) return <Panel className="font-mono text-[#71717A] text-sm">[ NO LEAGUE AVAILABLE ]</Panel>;
  if (loading) return <TactLoader label="LOADING STANDINGS" />;

  return (
    <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] overflow-x-auto" data-testid="standings-table">
      <table className="w-full min-w-[760px]">
        <thead>
          <tr className="border-b border-[#E4E4E7] dark:border-[#2A2A30]">
            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">#</th>
            <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("st.headerPlayer")}</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">P</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">W</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">D</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">L</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">GF</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">GA</th>
            <th className="text-right px-3 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">GD</th>
            <th className="text-right px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">Pts</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-10 text-center font-mono text-[#71717A] text-sm">
                {t("st.noResults")}
              </td>
            </tr>
          )}
          {rows.map((r) => {
            const top = r.rank === 1;
            const isMe = myId && r.user_id === myId;
            const medal = r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : null;
            const nameCls = isMe ? "text-[#00FF87]" : top ? "text-[#FFD700]" : "text-[#09090B] dark:text-[#F4F4F5]";
            const rowBg = top
              ? "bg-[#FFD700]/10"
              : r.rank === 2
                ? "bg-[#CBD5E1]/10 dark:bg-[#94A3B8]/10"
                : r.rank === 3
                  ? "bg-[#B45309]/10"
                  : isMe ? "bg-[#00FF87]/5" : "";
            const rowBorder = top
              ? "border-l-4 border-l-[#FFD700] shadow-[inset_4px_0_0_rgba(255,215,0,0.35)]"
              : r.rank === 2
                ? "border-l-4 border-l-[#94A3B8]"
                : r.rank === 3
                  ? "border-l-4 border-l-[#B45309]"
                  : "";
            return (
              <tr
                key={r.user_id}
                className={`border-b border-[#E4E4E7] dark:border-[#2A2A30] ${rowBg} ${rowBorder} transition-colors`}
                data-testid={`standings-row-${r.rank}`}
              >
                <td className={`px-4 py-3 font-mono ${top ? "text-[#FFD700]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {medal && (
                      <span
                        className="text-base leading-none"
                        data-testid={`standings-medal-${r.rank}`}
                        aria-label={`rank ${r.rank}`}
                      >
                        {medal}
                      </span>
                    )}
                    <span>#{r.rank}</span>
                  </span>
                </td>
                <td className={`px-4 py-3`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/players/${encodeURIComponent(r.username)}`} className={`font-heading text-base uppercase tracking-wide hover:underline ${nameCls}`} data-testid={`standings-profile-${r.rank}`}>{r.username}{isMe ? " ◂ you" : ""}</Link>
                    {r.reliability !== null && r.reliability !== undefined && (
                      <ReliabilityBadge score={r.reliability} flagged={r.reliability_flagged} size="sm" testId={`standings-reliability-${r.rank}`} />
                    )}
                    <AdminRemovePlayerButton
                      userId={r.user_id}
                      username={r.username}
                      targetRole="player"
                      size="xs"
                      testId={`standings-remove-${r.rank}`}
                      onRemoved={() => setRows((prev) => prev.filter((x) => x.user_id !== r.user_id))}
                    />
                  </div>
                  <div className="font-mono text-[10px] text-[#71717A]">{r.game_id}</div>
                </td>
                <td className="px-3 py-3 text-right font-mono">{r.played}</td>
                <td className="px-3 py-3 text-right">
                  <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 font-mono text-xs font-bold bg-[#00FF87]/15 text-[#059669] dark:text-[#00FF87] border border-[#00FF87]/30 rounded-sm" data-testid={`standings-w-${r.rank}`}>
                    {r.won}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-mono">{r.drawn}</td>
                <td className="px-3 py-3 text-right">
                  <span className="inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 font-mono text-xs font-bold bg-[#E11D48]/15 text-[#E11D48] border border-[#E11D48]/30 rounded-sm" data-testid={`standings-l-${r.rank}`}>
                    {r.lost}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-mono">{r.gf}</td>
                <td className="px-3 py-3 text-right font-mono">{r.ga}</td>
                <td className={`px-3 py-3 text-right font-mono ${r.gd > 0 ? "text-[#00FF87]" : r.gd < 0 ? "text-[#E11D48]" : ""}`}>{r.gd > 0 ? `+${r.gd}` : r.gd}</td>
                <td className={`px-4 py-3 text-right font-mono font-bold ${top ? "text-[#FFD700]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>{r.points}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Standings({ activeLeague }) {
  const { t } = useT();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div data-testid="standings-page">
      <SectionTitle kicker={activeLeague ? `// ${activeLeague.name}` : "//"} testId="standings-title">{t("st.title")}</SectionTitle>
      {isAdmin
        ? <AdminStandingsBrowser fallbackLeagueId={activeLeague?.id} />
        : <StandingsTable leagueId={activeLeague?.id} />}
    </div>
  );
}

/**
 * Admin-only league browser for the Standings page. Splits every league
 * into MOBILE / CONSOLE platform tabs and lets the admin pick any specific
 * league via a dropdown to inspect that season's standings.
 *
 * Sorts leagues newest-first within each platform tab.
 */
function AdminStandingsBrowser({ fallbackLeagueId }) {
  const { t } = useT();
  const [leagues, setLeagues] = useState(null); // null = loading
  const [platform, setPlatform] = useState("mobile");
  const [leagueId, setLeagueId] = useState(fallbackLeagueId || "");
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/leagues");
        if (!alive) return;
        const list = Array.isArray(data) ? data : [];
        setLeagues(list);
        // If no fallback was provided OR fallback is for a different platform,
        // default to the newest league of the active platform.
        const fb = fallbackLeagueId && list.find((l) => l.id === fallbackLeagueId);
        if (fb) {
          setPlatform((fb.platform || "mobile").toLowerCase());
          setLeagueId(fb.id);
        } else {
          const firstMobile = list.find((l) => (l.platform || "mobile").toLowerCase() === "mobile");
          if (firstMobile) setLeagueId(firstMobile.id);
        }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Failed to load leagues");
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  if (leagues === null) return <TactLoader label={t("admStand.loading")} />;
  if (error) return <Panel className="font-mono text-[#E11D48] text-sm" testId="admin-standings-error">[ {error} ]</Panel>;

  const norm = (p) => (p || "mobile").toLowerCase();
  const mobileLeagues = leagues
    .filter((l) => norm(l.platform) === "mobile")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const consoleLeagues = leagues
    .filter((l) => norm(l.platform) === "console")
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const activeList = platform === "mobile" ? mobileLeagues : consoleLeagues;

  // Auto-select first league of the active platform if current selection
  // doesn't belong to it.
  const activeIds = new Set(activeList.map((l) => l.id));
  if (leagueId && !activeIds.has(leagueId) && activeList.length > 0) {
    // Defer to a microtask via setState to avoid render-time set warnings.
    setTimeout(() => setLeagueId(activeList[0].id), 0);
  }

  const switchPlatform = (p) => {
    setPlatform(p);
    const list = p === "mobile" ? mobileLeagues : consoleLeagues;
    if (list.length > 0) setLeagueId(list[0].id);
    else setLeagueId("");
  };

  const statusLabel = (l) => {
    const s = (l.status || "").toUpperCase();
    return s ? `· ${s}` : "";
  };

  return (
    <div className="space-y-5" data-testid="admin-standings-browser">
      {/* Platform tabs */}
      <div className="flex items-center gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1 inline-flex" data-testid="admin-standings-platform-tabs">
        <button
          type="button"
          onClick={() => switchPlatform("mobile")}
          data-testid="admin-standings-tab-mobile"
          className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
            platform === "mobile" ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          {t("admStand.mobile")} <span className="font-score text-sm">{mobileLeagues.length}</span>
        </button>
        <button
          type="button"
          onClick={() => switchPlatform("console")}
          data-testid="admin-standings-tab-console"
          className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${
            platform === "console" ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          {t("admStand.console")} <span className="font-score text-sm">{consoleLeagues.length}</span>
        </button>
      </div>

      {/* League dropdown */}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]" htmlFor="admin-standings-league-select">
          {t("admStand.pickLeague")}
        </label>
        <select
          id="admin-standings-league-select"
          data-testid="admin-standings-league-select"
          value={leagueId}
          onChange={(e) => setLeagueId(e.target.value)}
          className="bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-3 py-2 font-mono text-[11px] text-[#09090B] dark:text-[#F4F4F5] focus:outline-none focus:border-[#7C3AED] min-w-[280px]"
        >
          {activeList.length === 0 ? (
            <option value="">{t("admStand.empty")}</option>
          ) : (
            activeList.map((l) => (
              <option key={l.id} value={l.id}>
                {(l.name || "UNNAMED")} {statusLabel(l)} · {l.player_count}/{l.capacity}
              </option>
            ))
          )}
        </select>
      </div>

      {leagueId
        ? <StandingsTable leagueId={leagueId} />
        : <Panel className="font-mono text-[#71717A] text-sm" testId="admin-standings-empty-platform">{t("admStand.noLeagues")}</Panel>}
    </div>
  );
}

export { StandingsTable };
